import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions
} from "@tanstack/react-query";
import api from "../../api";
import { camelize } from "../../utils/SnakeCamelConversion";

interface Response<T> {
  id: number;
  processes: Array<T>;
}

interface UseChangeProcessesProps<T> {
  queryKey: string;
  queryUrl: string;
  siteId: number | undefined;
  options?: Partial<UseQueryOptions<Array<T> | undefined>>;
}

function useChangeProcesses<T extends { id: number }>({
  queryKey,
  queryUrl,
  siteId,
  options
}: UseChangeProcessesProps<T>) {
  const { data, isLoading, error } = useQuery({
    queryKey: [queryKey, { siteId, queryUrl }],
    queryFn: () => fetchChanges<T>(siteId, queryUrl),
    ...options,
    enabled: !!siteId && (!options || options.enabled)
  });

  return { data, isLoading, error };
}

async function fetchChanges<T extends { id: number }>(
  siteId: number | undefined,
  queryUrl: string
) {
  if (typeof siteId === "undefined") {
    return undefined;
  }

  const response = await api.get<Response<T>>(queryUrl);

  const processes = convertMissingDataFields(response.data.processes);
  return processes.sort((a, b) => a.id - b.id);
}

function convertMissingDataFields(data) {
  const formattedData = data;
  formattedData.forEach((process) => {
    Object.keys(process.steps).forEach((step) => {
      process.steps[step]?.missingData?.forEach((missingData, dataIndex) => {
        missingData.missingFields.forEach((missingField, fieldIndex) => {
          process.steps[step].missingData[dataIndex].missingFields[fieldIndex] =
            camelize(missingField);
        });
      });
      process.steps[step]?.exclusiveOrFields?.forEach((group, groupIndex) => {
        group.forEach((field, index) => {
          process.steps[step].exclusiveOrFields[groupIndex][index] =
            camelize(field);
        });
      });
    });
  });
  return formattedData;
}

function useChangeMutation<T>(
  siteId: number,
  mutationUrl: string,
  queryKey: string
) {
  const queryClient = useQueryClient();

  function mutateOperatorChange<T>(change: T, siteId: number) {
    return api.post(mutationUrl, {
      id: siteId,
      processes: [change]
    });
  }

  return useMutation({
    mutationFn: (change: T) => mutateOperatorChange(change, siteId),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: [queryKey, { siteId }]
      });
    }
  });
}

export { useChangeMutation, useChangeProcesses };
